import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Lightning, CheckCircle, ArrowClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function SnapLinkAddon({ orgId, isOwner, onChanged }) {
  const [q, setQ] = useState(null);
  const [busy, setBusy] = useState(false);
  const [polling, setPolling] = useState(false);

  const load = useCallback(async () => {
    if (!orgId) return;
    try {
      const res = await api.get(`/orgs/${orgId}/support/quota`);
      setQ(res.data);
    } catch { /* ignore */ }
  }, [orgId]);

  useEffect(() => { load(); }, [load]);

  // Handle return from Stripe checkout for the add-on.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sid = params.get("snaplink_session_id");
    if (!sid) return;
    setPolling(true);
    let tries = 0;
    const iv = setInterval(async () => {
      tries += 1;
      try {
        const res = await api.get(`/billing/snaplink-status/${sid}`);
        if (res.data.status === "complete") {
          clearInterval(iv); setPolling(false);
          toast.success("Controlly Snap Unlimited is now active 🎉");
          window.history.replaceState({}, "", "/billing");
          load(); onChanged?.();
        }
      } catch { /* keep trying */ }
      if (tries > 20) { clearInterval(iv); setPolling(false); window.history.replaceState({}, "", "/billing"); }
    }, 2000);
    return () => clearInterval(iv);
  }, [load, onChanged]);

  const subscribe = async () => {
    if (!isOwner) { toast.error("Only the workspace owner can change billing."); return; }
    setBusy(true);
    try {
      const res = await api.post(`/orgs/${orgId}/billing/snaplink-checkout`, { origin_url: window.location.origin });
      window.location.href = res.data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start checkout");
      setBusy(false);
    }
  };

  const cancel = async () => {
    if (!isOwner) { toast.error("Only the workspace owner can change billing."); return; }
    setBusy(true);
    try {
      await api.post(`/orgs/${orgId}/billing/snaplink-cancel`);
      toast.success("Controlly Snap add-on will end at the end of your billing period");
      load(); onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not cancel");
    } finally { setBusy(false); }
  };

  if (!q) return null;

  return (
    <div className="mb-6 rounded-xl border border-primary/30 bg-primary/5 p-5 sm:p-6" data-testid="snaplink-addon-card">
      <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:justify-between">
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 rounded-lg bg-primary/15 flex items-center justify-center shrink-0">
            <Lightning size={20} weight="fill" className="text-blue-400" />
          </div>
          <div>
            <div className="font-heading text-base font-bold flex items-center gap-2">
              Controlly Snap Unlimited
              {q.included && <span className="text-[10px] uppercase tracking-wider bg-emerald-500/15 text-emerald-400 px-2 py-0.5 rounded-full">Included in your plan</span>}
              {q.addon && !q.included && <span className="text-[10px] uppercase tracking-wider bg-blue-500/15 text-blue-300 px-2 py-0.5 rounded-full">Active</span>}
            </div>
            <p className="text-sm text-zinc-400 mt-0.5 max-w-md">
              {q.included
                ? "Your plan already includes unlimited one-time support sessions — no add-on needed."
                : q.addon
                  ? (q.cancel_at_period_end
                      ? "Active until the end of your billing period, then it won't renew."
                      : "Unlimited one-time support sessions. Renews monthly.")
                  : <>Unlock <span className="text-zinc-200 font-medium">unlimited</span> one-time support sessions for <span className="text-zinc-200 font-medium">${q.addon_price}/mo</span>. You currently have {q.remaining} of {q.limit} free trial sessions left.</>}
            </p>
          </div>
        </div>

        <div className="shrink-0">
          {polling ? (
            <div className="flex items-center gap-2 text-sm text-blue-300"><ArrowClockwise size={16} className="animate-spin" /> Verifying…</div>
          ) : q.included ? (
            <span className="flex items-center gap-1.5 text-sm text-emerald-400"><CheckCircle size={18} weight="fill" /> Unlimited</span>
          ) : q.addon ? (
            !q.cancel_at_period_end && (
              <Button variant="ghost" size="sm" onClick={cancel} disabled={busy || !isOwner}
                data-testid="snaplink-cancel-button" className="text-red-400 hover:text-red-300 hover:bg-red-500/10">
                {busy ? "…" : "Cancel add-on"}
              </Button>
            )
          ) : (
            <Button onClick={subscribe} disabled={busy || !isOwner}
              data-testid="snaplink-subscribe-button" className="bg-blue-600 hover:bg-blue-500">
              {busy ? "…" : `Add for $${q.addon_price}/mo`}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
